import {
  FormController,
  FormFileUpload,
  FormTextArea,
} from '@/components/form';
import {
  ATTACHMENT_MAX_FILE_SIZE_BYTES,
  ATTACHMENT_MAX_FILE_SIZE_LABEL,
} from '@/constants/uploadLimits';
import { useAppSelector } from '@/state';
import { RfiApi } from '@/state/api';
import {
  Badge,
  Box,
  Button,
  FileUploadFileMimeType,
  Flex,
  Separator,
  Text,
} from '@chakra-ui/react';
import { Avatar, Card, Input, toasterApi } from '@ui';
import dayjs from 'dayjs';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { FaRegClock } from 'react-icons/fa6';
import { FiFileText, FiMessageSquare } from 'react-icons/fi';
import { SiTicktick } from 'react-icons/si';
import z from 'zod';

type ConversationProps = {
  rfi_id: number;
};

const Conversation = ({ rfi_id }: ConversationProps) => {
  const currentUser = useAppSelector((state) => state.auth.current.user);

  // POST - create message
  const [postMessage] = RfiApi.Api.usePostMessageMutation();

  // POST - select message as final answer
  const [postMessageFinalAnswer] =
    RfiApi.Api.usePostMessageFinalAnswerMutation();

  // POST - upload attachments
  const [postRfiAttachmentS3PreSignMutation] =
    RfiApi.Api.usePostRfiAttachmentS3PreSignMutation(); // get presigned url from S3
  const [putRfiAttachmentUploadToS3Mutation] =
    RfiApi.Api.usePutRfiAttachmentUploadToS3Mutation(); // upload to S3

  const { data: allMessages } = RfiApi.Api.useGetMessagesQuery({
    rfi_id,
  });
  const officialAnswer = allMessages?.find((message) =>
    Boolean(message.is_final_answer),
  );
  const uniqueUsers = Array.from(
    new Set(allMessages?.map((message) => message.author)),
  );

  const handlePostMessage = async (formData: ConversationFormType) => {
    let preSignedS3UrlsForAttachments:
      | {
          filename: string;
          upload_url: string;
          file_url: string;
        }[]
      | undefined;

    if (formData?.attachments?.length) {
      try {
        const attachmentsFileNameAndContent =
          formData?.attachments?.map((attachment) => ({
            filename: attachment?.name,
            content_type: attachment?.type,
            size: attachment?.size,
          })) ?? [];

        // Get S3 presigned urls
        const signedS3Urls = await postRfiAttachmentS3PreSignMutation({
          files: attachmentsFileNameAndContent,
        });

        // Upload to S3 using PUT request
        await Promise.all(
          (formData.attachments ?? []).map(async (attachment) => {
            const fileUploadUrl = signedS3Urls?.data?.presigned_urls?.find(
              (url) => url.filename === attachment.name,
            )?.upload_url;

            putRfiAttachmentUploadToS3Mutation({
              content_type: attachment.type,
              file: attachment,
              s3_url: fileUploadUrl!,
            });
          }),
        );

        preSignedS3UrlsForAttachments = signedS3Urls?.data?.presigned_urls;
      } catch {
        toasterApi({
          type: 'error',
          status: 500,
          code: 'Error',
          message: 'Could not upload attachments',
        });
      }
    }

    try {
      await postMessage({
        rfi_id,
        body: {
          message: formData?.message,
          bic_user: currentUser?.id as number,
          attachments:
            preSignedS3UrlsForAttachments?.map((attachment) => ({
              filename: attachment.filename,
              file: attachment.file_url,
            })) ?? [],
        },
      });

      toasterApi({
        type: 'success',
        status: 201,
        code: 'Success',
        message: 'Message created successfully',
      });
    } catch {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message: 'Could not create message',
      });
    }
  };

  const handlePostMessageFinalAnswer = (message_id: number) => {
    postMessageFinalAnswer({
      rfi_id,
      message_id,
    })
      .then(() => {
        toasterApi({
          type: 'success',
          status: 201,
          code: 'Success',
          message: 'Message successfully selected as final answer',
        });
      })
      .catch(() => {
        toasterApi({
          type: 'error',
          status: 500,
          code: 'Error',
          message: 'Could not select message as final answer',
        });
      });
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex justifyContent="space-between">
          <Flex flexDirection="column" gapY={2}>
            <Flex gapX={2}>
              <Text fontWeight="bold">RFI - {rfi_id}</Text>
              <Badge colorPalette="orange" borderRadius="full">
                <Text>Open</Text>
              </Badge>
            </Flex>
            <Text fontSize="sm" color="fg.subtle">
              Aluminum extrusion
            </Text>
          </Flex>
          <Flex gapX={2} alignItems="center">
            <Text fontSize="xs" color="fg.subtle">
              Ball in Court
            </Text>
            <Badge
              color="fg"
              bg="border.emphasized"
              borderRadius="full"
              paddingY={2}
              paddingX={4}
            >
              <Flex alignItems="center" gapX={2}>
                <Avatar
                  name="Engineer A"
                  size="xs"
                  bg="border.inverted"
                  color="fg.inverted"
                />
                <Flex flexDirection="column" gapY={2}>
                  <Text>Engineer A</Text>
                  <Text>Engineer</Text>
                </Flex>
              </Flex>
            </Badge>

            <Flex gapX={2} alignItems="center">
              <Text fontSize="xs" color="fg.subtle">
                Due
              </Text>
              <Badge color="fg" bg="border.emphasized" borderRadius="full">
                <FaRegClock />
                <Text>Nov 23</Text>
              </Badge>
            </Flex>
          </Flex>
          <Flex gapX={2} alignItems="center" fontSize="sm">
            <Text>Status</Text>
            <Button
              size="sm"
              variant="outline"
              borderColor="bg.emphasized"
              color="fg"
            >
              Assign
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderColor="bg.emphasized"
              color="fg"
            >
              Export
            </Button>
          </Flex>
        </Flex>
      </Card>
      <Flex justifyContent="space-between" gapX={4}>
        <Flex flex={2} flexDirection="column" gapY={4}>
          <Card>
            <Flex justifyContent="space-between">
              <Flex gapX={2} alignItems="center">
                <FiMessageSquare />
                <Text fontWeight="bold">Thread</Text>
              </Flex>
              <Flex gapX={2}>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="bg.emphasized"
                  color="fg"
                >
                  Mark all read
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="bg.emphasized"
                  color="fg"
                >
                  Export Thread
                </Button>
              </Flex>
            </Flex>
          </Card>
          <Flex gapX={2}>
            <Button
              variant="outline"
              borderColor="bg.emphasized"
              color="fg.inverted"
              bg="fg"
            >
              All
            </Button>
            <Button variant="outline" borderColor="bg.emphasized" color="fg">
              Supplier Replies
            </Button>
            <Button variant="outline" borderColor="bg.emphasized" color="fg">
              Internal
            </Button>
            <Button variant="outline" borderColor="bg.emphasized" color="fg">
              Attachments
            </Button>
            <Button variant="outline" borderColor="bg.emphasized" color="fg">
              Unread
            </Button>
            <Button variant="outline" borderColor="bg.emphasized" color="fg">
              Open Issues
            </Button>
          </Flex>

          {allMessages?.map((message, index) => (
            <Card
              key={`rfi-submittal-conversation-message-${message?.id}-${index}`}
            >
              <Flex>
                <Flex gapX={2}>
                  <Avatar name="Engineer A" bg="fg.subtle" color="fg" />
                  <Flex flexDirection="column" gapY={4}>
                    <Flex flexDirection="column" gapY={2}>
                      <Flex justifyContent="space-between">
                        <Flex gapX={2} alignItems="center">
                          <Text>{message?.author}</Text>
                          <Badge color="fg" bg="border.emphasized">
                            <Text>Engineer</Text>
                          </Badge>
                          <Badge colorPalette="blue">
                            <Text>RFI Question</Text>
                          </Badge>
                        </Flex>
                        <Button
                          size="sm"
                          bg="bg.emphasized"
                          color="fg"
                          onClick={() => {
                            handlePostMessageFinalAnswer(message?.id);
                          }}
                        >
                          Mark As Answer
                        </Button>
                      </Flex>
                      <Text fontSize="xs" color="fg.subtle">
                        {dayjs(message?.created_at).format(
                          'DD/MM/YYYY, h:mm:ss A',
                        )}
                      </Text>
                    </Flex>
                    <Text fontSize="sm">{message?.message}</Text>

                    <Badge
                      width="fit-content"
                      padding={2}
                      bg="border.emphasized"
                      color="fg"
                    >
                      <Flex gapX={2} alignItems="center">
                        <FiFileText />
                        <Text>SK-31.pdf</Text>
                      </Flex>
                    </Badge>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          ))}

          {/* Optional note */}
          <Card>
            <Flex>
              <FormController
                size="lg"
                formSchema={conversationFormSchema}
                defaultValues={{
                  message: '',
                  attachments: [],
                }}
                onFormSubmit={handlePostMessage}
                buttonProps={{ hidden: true }}
              >
                {({
                  register,
                  control,
                  errors,
                }: {
                  register: UseFormRegister<ConversationFormType>;
                  control: Control<ConversationFormType>;
                  errors: FieldErrors<ConversationFormType>;
                }) => {
                  return (
                    <Flex flexDirection="column">
                      <FormTextArea
                        register={register}
                        control={control}
                        errors={errors}
                        id="message"
                        label="Message"
                        placeholder="Add an internal note or answer... @mention people, add part/ lot/ WO context"
                        showLabelAtTop
                      />

                      <Box maxWidth="160px">
                        <FormFileUpload
                          control={control}
                          register={register}
                          errors={errors}
                          id="attachments"
                          label=""
                          maxFiles={10}
                          maxFileSize={ATTACHMENT_MAX_FILE_SIZE_BYTES}
                          accept={
                            'application/*,image/*,text/plain,text/csv' as FileUploadFileMimeType
                          }
                        />
                      </Box>
                      <Flex gapX={3} alignItems="center">
                        <Flex gapX={2} alignItems="center">
                          <Text>Internal</Text>
                        </Flex>
                        <Flex gapX={2} alignItems="center">
                          <Text>Assign</Text>
                          <Text>Choose</Text>
                        </Flex>
                        <Flex gapX={2} alignItems="center">
                          <Text>Status</Text>
                          <Input />
                        </Flex>
                        <Button size="sm" type="submit">
                          Send
                        </Button>
                      </Flex>
                    </Flex>
                  );
                }}
              </FormController>
            </Flex>
          </Card>
        </Flex>
        <Flex flex={1} flexDirection="column" gapY={4}>
          <Card>
            <Flex flexDirection="column" gapY={4}>
              <Flex gapX={2} alignItems="center">
                <SiTicktick />
                <Text fontSize="sm">Official answer</Text>
              </Flex>
              <Separator />
              <Text fontSize="sm">
                {officialAnswer?.message || 'No official answer'}
              </Text>
              <Badge
                width="fit-content"
                padding={2}
                bg="border.emphasized"
                color="fg"
              >
                <Flex gapX={2} alignItems="center">
                  <FiFileText />
                  <Text>SK-31.pdf</Text>
                </Flex>
              </Badge>
              <Text fontSize="xs" color="fg.subtle">
                Set from answer by {officialAnswer?.author} on{' '}
                {dayjs(officialAnswer?.created_at).format(
                  'DD/MM/YYYY, h:mm:ss A',
                )}
              </Text>
            </Flex>
          </Card>
          <Card>
            <Flex flexDirection="column" gapY={2}>
              <Flex gapX={2} alignItems="center">
                <FiMessageSquare />
                <Text fontSize="sm">Participants</Text>
              </Flex>
              <Separator />
              {uniqueUsers?.map((user, index) => (
                <Flex
                  key={`rfi-submittal-conversation-message-participants-${user}-${index}`}
                  gapX={2}
                  alignItems="center"
                >
                  <Avatar name="Engineer A" bg="fg.subtle" color="fg" />
                  <Flex flexDirection="column" gapY={2}>
                    <Text>{user}</Text>
                    <Text fontSize="xs" color="fg.subtle">
                      Engineer
                    </Text>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </Flex>
  );
};
export default Conversation;

const conversationFormSchema = z.object({
  message: z.string().nonempty({ error: 'Message is required' }),
  attachments: z
    .array(z.instanceof(File))
    .refine(
      (files) => files.every((f) => f.size <= ATTACHMENT_MAX_FILE_SIZE_BYTES),
      { message: `Each file must be ${ATTACHMENT_MAX_FILE_SIZE_LABEL} or less.` },
    )
    .optional(),
});
type ConversationFormType = z.infer<typeof conversationFormSchema>;
